/**
 * Pixel values are flow coordinates (1 px ≈ 1 flow unit at zoom 1). Routing
 * penalties are unitless costs summed with the route's length in pixels.
 */
export const GEOMETRY = {
  /** *Maximum* distance a stub extends from a terminal — `clampStub` shortens
   *  it near a neighbouring body. Kept under `OBSTACLE_PADDING`. */
  STUB: 18,

  /** Clearance between a shortened stub's tip and the body that shortened it. */
  STUB_CLEARANCE: 2,

  /** Clearance around every component obstacle. Larger than half a lane stack,
   *  so lane separation can't nudge a tidied cable back onto the body. */
  OBSTACLE_PADDING: 20,

  /** Padding around the cable's *own* endpoint components, interior segments
   *  only — the first and last stubs touch their own terminal's body. */
  SELF_PADDING: 10,

  /** Extra clearance past the inflated obstacle edge for elbow candidates. */
  ELBOW_MARGIN: 8,

  /** Perpendicular separation between parallel cables sharing a corridor. Also
   *  the parallel-snap distance during a manual segment drag. */
  LANE_SPACING: 10,

  /** Preferred space between the positive and negative groups in a shared
   *  corridor. A tight corridor may use ordinary lane spacing instead. */
  POLARITY_LANE_SPACING: 20,

  /** Snap radius for the manual segment drag's parallel snap. Under
   *  LANE_SPACING, so a segment can still be placed between two lanes. */
  SNAP_PARALLEL_PX: 6,

  /** Minimum gap between terminals on one side; below it they spill onto a
   *  perpendicular side. */
  MIN_TERMINAL_GAP_PX: 22,

  /** Terminals are placed in [SIDE_MARGIN, 1 − SIDE_MARGIN] of a side, so they
   *  never sit in a rounded corner. */
  SIDE_MARGIN: 0.1,

  /** Half the junction's rendered size — it draws as a 2 × JUNCTION_HALF box,
   *  and everything else derives its centre from its top-left with this. */
  JUNCTION_HALF: 7,

  /** Fan-out anchor for cables sharing a junction cardinal. Both must stay small
   *  enough that the closing diagonal hides in the rounded-corner radius. */
  JUNCTION_FAN_RADIUS: 8,
  JUNCTION_FAN_SPREAD: 5,

  /** On a node move, drop the cable's waypoints when the path exceeds this
   *  multiple of the direct Manhattan distance *and* this many pixels of slack. */
  WAYPOINT_PRUNE_RATIO: 1.5,
  WAYPOINT_PRUNE_SLACK_PX: 40,

  /** Per-collision cost, high enough that any obstacle-free candidate strictly
   *  beats any obstacle-hitting one. Measured against the *padded* rect. */
  COLLISION_PENALTY: 1_000_000,

  /** Extra cost when a route enters a component's BODY rather than merely its
   *  padding. Set so one body crossing outweighs four clearance violations. */
  BODY_PENALTY: 4_000_000,

  /** Cost per crossing of an already-routed cable. Under COLLISION_PENALTY —
   *  cross a cable rather than a component — but over ~30 px of detour. */
  CROSSING_PENALTY: 400,

  /** Per-bend cost: a 90° corner is worth ~15 px of straightening. */
  BEND_PENALTY: 15,

  /** Per-pixel reward for running parallel to a corridor the router was biased
   *  toward. At 1.0 px⁻¹ it exactly cancels the length cost over the overlap, so
   *  a longer route wins only when matched by extra overlap. */
  CORRIDOR_OVERLAP_BONUS: 1.0,
  CORRIDOR_MATCH_BAND_PX: 16,

  /** Bonus when this cable's mid-segment sits on the exact x/y of a
   *  previously-routed one. Over BEND_PENALTY, under CROSSING_PENALTY — so it
   *  never aligns itself into a crossing. */
  SHARED_ELBOW_BONUS: 25,

  /** How much longer than direct a route may run and still collect the bundling
   *  rewards, which scale with overlap: without the gate a short cable is
   *  dragged onto a distant trunk and back. */
  CORRIDOR_DETOUR_SLACK_PX: 40,

  /** After every pass a route BOTH over RATIO× the direct Manhattan distance
   *  AND over SLACK px longer than direct is replaced by a plain
   *  obstacle-avoiding route. Real detours top out around 2–2.3×. */
  DETOUR_CLAMP_RATIO: 3,
  DETOUR_CLAMP_SLACK_PX: 120,

  /** Cost to cross a zone wall with neither endpoint inside that zone. Between
   *  CROSSING_PENALTY and COLLISION_PENALTY: detour when possible. */
  ZONE_WALL_PENALTY: 5_000,

  /** A zone wall is a line, but `segmentInsideRect` only registers strict
   *  interior overlap, so obstacle checks widen it into a rect this thick. */
  ZONE_WALL_THICKNESS: 2,
} as const;
